// File: src/pages/ResetPassword.js

import React, { useState, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { FaEye, FaEyeSlash } from 'react-icons/fa'
import './ResetPassword.css'
import { get, post } from '../api'

function getPasswordStrength(pw) {
  let score = 0
  if (pw.length >= 8) score++
  if (/[A-Z]/.test(pw)) score++
  if (/[a-z]/.test(pw)) score++
  if (/\d/.test(pw)) score++
  if (/[^A-Za-z0-9]/.test(pw)) score++
  return Math.min(score, 4)
}

export default function ResetPassword() {
  const [searchParams] = useSearchParams()
  const token          = searchParams.get('token') || ''
  const uid            = searchParams.get('uid') || ''
  const navigate       = useNavigate()

  const [newPwd, setNewPwd]         = useState('')
  const [confirmPwd, setConfirmPwd] = useState('')
  const [showNew, setShowNew]       = useState(false)
  const [showConf, setShowConf]     = useState(false)
  const [strength, setStrength]     = useState(0)
  const [status, setStatus]         = useState('idle')
  const [error, setError]           = useState(null)
  const [tokenValid, setTokenValid] = useState(null)

  useEffect(() => {
    setStrength(getPasswordStrength(newPwd))
  }, [newPwd])

  useEffect(() => {
    if (!token || !uid) {
      setTokenValid(false)
      setError('This reset link is invalid or incomplete. Please request a new password reset.')
      return
    }

    let cancelled = false

    async function validateToken() {
      try {
        const res = await get('/auth/validate-reset-token', {
          params: { token, uid }
        })
        if (cancelled) return
        setTokenValid(Boolean(res.data?.valid))
        if (!res.data?.valid) {
          setError('This reset link is invalid or has expired. Please request a new password reset.')
        }
      } catch {
        if (cancelled) return
        setTokenValid(false)
        setError('Unable to validate reset link. Please try again or request a new reset.')
      }
    }

    validateToken()
    return () => { cancelled = true }
  }, [token, uid])

  const passwordsMatch = confirmPwd.length > 0 && newPwd === confirmPwd
  const canSubmit =
    tokenValid === true &&
    strength >= 2 &&
    newPwd.length >= 8 &&
    passwordsMatch

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    setStatus('pending')

    if (!token || !uid) {
      setError('This reset link is invalid or incomplete.')
      return setStatus('error')
    }
    if (newPwd.length < 8) {
      setError('Password must be at least 8 characters long')
      return setStatus('error')
    }
    if (strength < 2) {
      setError('Password is too weak')
      return setStatus('error')
    }
    if (!passwordsMatch) {
      setError('Passwords do not match')
      return setStatus('error')
    }

    try {
      await post('/auth/reset-password', { token, uid, newPassword: newPwd })

      setStatus('success')
      setTimeout(() => navigate('/login', { replace: true }), 1500)
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Reset failed'
      setError(msg)
      setStatus('error')
    }
  }

  const labels = ['Too weak', 'Weak', 'Fair', 'Good', 'Strong']

  if (tokenValid === false) {
    return (
      <div className="reset-password-page">
        <form className="reset-password-form">
          <h2>Set a New Password</h2>
          <div className="message error">{error}</div>
          <div className="back-to-login" style={{ marginTop: '1rem' }}>
            <Link to="/forgot-password">Request a new reset link</Link>
            {' · '}
            <Link to="/login">Back to Log In</Link>
          </div>
        </form>
      </div>
    )
  }

  return (
    <div className="reset-password-page">
      <form className="reset-password-form" onSubmit={handleSubmit}>
        <h2>Set a New Password</h2>

        {status === 'success' ? (
          <div className="message success">
            Your password has been reset. Redirecting to login…
          </div>
        ) : (
          <>
            {tokenValid === null && (
              <div className="message">Validating reset link…</div>
            )}

            {error && <div className="message error">{error}</div>}

            <label htmlFor="newPwd">New Password</label>
            <div style={{ position: 'relative', marginTop: '.5rem' }}>
              <input
                id="newPwd"
                type={showNew ? 'text' : 'password'}
                value={newPwd}
                onChange={e => setNewPwd(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                disabled={tokenValid !== true}
                style={{
                  width: '100%',
                  padding: '0.5rem .75rem',
                  paddingRight: '2.5rem',
                  border: '1px solid #ccc',
                  borderRadius: '4px'
                }}
              />
              <button
                type="button"
                onClick={() => setShowNew(v => !v)}
                aria-label={showNew ? 'Hide password' : 'Show password'}
                tabIndex={-1}
                style={{
                  position: 'absolute',
                  top: '50%',
                  right: '0.75rem',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#666',
                  fontSize: '1.2rem',
                  padding: 0,
                  zIndex: 2
                }}
              >
                {showNew ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            {newPwd && (
              <div className={`strength-meter strength-${strength}`}>
                <div className="meter-bar"></div>
                <div className="strength-label">{labels[strength]}</div>
              </div>
            )}

            <label htmlFor="confirmPwd">Confirm Password</label>
            <div style={{ position: 'relative', marginTop: '.5rem' }}>
              <input
                id="confirmPwd"
                type={showConf ? 'text' : 'password'}
                value={confirmPwd}
                onChange={e => setConfirmPwd(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                disabled={tokenValid !== true}
                style={{
                  width: '100%',
                  padding: '0.5rem .75rem',
                  paddingRight: '2.5rem',
                  border: '1px solid #ccc',
                  borderRadius: '4px'
                }}
              />
              <button
                type="button"
                onClick={() => setShowConf(v => !v)}
                aria-label={showConf ? 'Hide password' : 'Show password'}
                tabIndex={-1}
                style={{
                  position: 'absolute',
                  top: '50%',
                  right: '0.75rem',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#666',
                  fontSize: '1.2rem',
                  padding: 0,
                  zIndex: 2
                }}
              >
                {showConf ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            {confirmPwd && (
              <div
                style={{
                  marginTop: '.25rem',
                  color: passwordsMatch ? 'green' : 'red',
                  fontSize: '0.9rem'
                }}
              >
                {passwordsMatch
                  ? 'Passwords match ✅'
                  : 'Passwords do not match ❌'}
              </div>
            )}

            <button
              type="submit"
              disabled={status === 'pending' || !canSubmit}
            >
              {status === 'pending' ? 'Updating…' : 'Reset Password'}
            </button>
          </>
        )}

        <div className="back-to-login" style={{ marginTop: '1rem' }}>
          <Link to="/login">← Back to Log In</Link>
        </div>
      </form>
    </div>
  )
}
